import React from "react";
import InlineEdit from '../../Components/InlineEdit';
import { IconButton } from '@mui/material';
import { Calendar, MapPin, FileText, Check, X } from 'lucide-react'; // Added icons for custom inputs
import { DatePicker } from '@mui/x-date-pickers';
import { Autocomplete, TextField } from '@mui/material';
import moment from 'moment';

class ReservationData extends React.Component {
    // Constructor and local state removed in favor of parent's handlePartialUpdate passed as props.onUpdate

    render() {
        const reservation = this.props.reservation;
        // const isEditing = this.props.isEditing; // Removed
        // const editedReservation = this.state.editedReservation || reservation; // Removed

        const statusColors = {
            ordered: 'bg-blue-100 text-blue-700 border-blue-200',
            onwater: 'bg-green-100 text-green-700 border-green-200',
            finished: 'bg-gray-100 text-gray-700 border-gray-200',
            canceled: 'bg-red-100 text-red-700 border-red-200',
        };

        const statusOptions = [
            { value: 'ordered', label: 'Objednaná' },
            { value: 'onwater', label: 'Na vode' },
            { value: 'canceled', label: 'Zrušená' },
            { value: 'finished', label: 'Ukončená' },
        ];

        return (
            <div className="bg-white shadow-sm border border-gray-100 p-5 md:p-6 hover:shadow transition-shadow">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-splavma-orange/10 rounded-xl flex items-center justify-center">
                            <Calendar className="w-5 h-5 text-splavma-orange" />
                        </div>
                        <h3 className="text-lg font-bold text-splavma-teal uppercase tracking-wide">Rezervácia</h3>
                    </div>
                </div>

                <div className="space-y-4">
                    {/* Status - Always Editable */}
                    <div className="flex items-start gap-3">
                        <div className="flex-1">
                            <InlineEdit
                                label="Stav"
                                alwaysEditing={true}
                                renderInput={({ save }) => (
                                    <select
                                        value={reservation.status}
                                        onChange={(e) => this.props.onUpdate({ status: e.target.value })}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all font-medium ${statusColors[reservation.status] || 'border-gray-300'}`}
                                    >
                                        {statusOptions.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </select>
                                )}
                            />
                        </div>
                    </div>

                    {/* Language - Always Editable */}
                    <div className="flex items-start gap-3">
                        <div className="flex-1">
                            <InlineEdit
                                label="Jazyk"
                                alwaysEditing={true}
                                renderInput={({ save }) => (
                                    <select
                                        value={reservation.language || 'sk'}
                                        onChange={(e) => this.props.onUpdate({ language: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all font-medium"
                                    >
                                        <option value="sk">Slovenský</option>
                                        <option value="en">Anglický</option>
                                    </select>
                                )}
                            />
                        </div>
                    </div>

                    {/* Dates - Grouped Inline Edit */}
                    <div className="group">
                        <InlineEdit
                            label="Termín"
                            hideLabel={true}
                            value={
                                <>
                                    <div className="flex items-start gap-3 mb-2">
                                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-50 transition-colors">
                                            <Calendar className="w-4 h-4 text-gray-600 group-hover:text-secondary-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Dĺžka splavu</p>
                                            <p className="text-base font-medium text-gray-900">{this.props.reservationLength} {this.props.reservationLength === 1 ? 'deň' : 'dni'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-50 transition-colors">
                                            <Calendar className="w-4 h-4 text-gray-600 group-hover:text-secondary-600" />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Termín</p>
                                            <p className="text-base font-medium text-gray-900">{this.props.dateText}</p>
                                        </div>
                                    </div>
                                </>
                            }
                            onSave={(newValues) => this.props.onUpdate(newValues)}
                            renderInput={({ save, cancel }) => {
                                // Local state for the inputs managed by a wrapper or just use standard state if we were functional. 
                                // Since we are in a render prop, we can't use hooks easily unless we extract specific component.
                                // BUT InlineEdit is functional, `renderInput` is just a callback. 
                                // We need a way to track the temp values while editing dates.
                                // InlineEdit DOES NOT manage the input values state for us, it only manages IS_EDITING.
                                // We need a small wrapper component for complex inputs to hold state!
                                return <DateRangeEditor
                                    startDate={reservation.start_date}
                                    endDate={reservation.end_date}
                                    onSave={save}
                                    onCancel={cancel}
                                />;
                            }}
                        />
                    </div>

                    {/* Canoe/Katamaran Counts - Grouped Inline Edit */}
                    <div className="group">
                        <InlineEdit
                            label="Lode"
                            hideLabel={true}
                            value={
                                <>
                                    <div className="flex items-start gap-3 mb-2">
                                        <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-50 transition-colors">
                                            <span className="text-sm font-bold text-gray-600 group-hover:text-secondary-600">🛶</span>
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Počet kanoe</p>
                                            <p className="text-base font-medium text-gray-900">
                                                {reservation.kanoe_count_info_min === reservation.kanoe_count_info_max
                                                    ? reservation.kanoe_count_info_min
                                                    : `${reservation.kanoe_count_info_min} - ${reservation.kanoe_count_info_max}`
                                                }
                                            </p>
                                        </div>
                                    </div>

                                    {(reservation.katamaran_count_info > 0) && (
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-secondary-50 transition-colors">
                                                <span className="text-sm font-bold text-gray-600 group-hover:text-secondary-600">⛵</span>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Počet katamaránov</p>
                                                <p className="text-base font-medium text-gray-900">{reservation.katamaran_count_info}</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            }
                            onSave={(newValues) => this.props.onUpdate(newValues)}
                            renderInput={({ save, cancel }) => (
                                <BoatsEditor
                                    kanoeMin={reservation.kanoe_count_info_min}
                                    kanoeMax={reservation.kanoe_count_info_max}
                                    katamaran={reservation.katamaran_count_info}
                                    onSave={save}
                                    onCancel={cancel}
                                />
                            )}
                        />
                    </div>

                    {/* Start/End Places */}
                    <div className="space-y-3">
                        <InlineEdit
                            label="Štart"
                            hideLabel={true}
                            value={
                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-success-50 transition-colors">
                                        <MapPin className="w-4 h-4 text-gray-600 group-hover:text-success-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Štart</p>
                                        <p className="text-base font-medium text-gray-900">{reservation.start_place || '-'}</p>
                                    </div>
                                </div>
                            }
                            onSave={(val) => this.props.onUpdate({ start_place: val })}
                            renderInput={({ save, cancel }) => (
                                <PlaceEditor
                                    label="Štart"
                                    value={reservation.start_place}
                                    places={this.props.places}
                                    onSave={save}
                                    onCancel={cancel}
                                />
                            )}
                        />

                        <InlineEdit
                            label="Cieľ"
                            hideLabel={true}
                            value={
                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-error-50 transition-colors">
                                        <MapPin className="w-4 h-4 text-gray-600 group-hover:text-error-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Cieľ</p>
                                        <p className="text-base font-medium text-gray-900">{reservation.end_place || '-'}</p>
                                    </div>
                                </div>
                            }
                            onSave={(val) => this.props.onUpdate({ end_place: val })}
                            renderInput={({ save, cancel }) => (
                                <PlaceEditor
                                    label="Cieľ"
                                    value={reservation.end_place}
                                    places={this.props.places}
                                    onSave={save}
                                    onCancel={cancel}
                                />
                            )}
                        />
                    </div>

                    {/* Note */}
                    <div>
                        <div className="h-px bg-gray-200 my-3"></div>
                        <InlineEdit
                            label="Poznámka"
                            hideLabel={true}
                            value={
                                <div className="flex items-start gap-3 group">
                                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-warning-50 transition-colors">
                                        <FileText className="w-4 h-4 text-gray-600 group-hover:text-warning-600" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Poznámka</p>
                                        {reservation.note ? (
                                            <p className="text-sm text-gray-700 bg-warning-50 rounded-lg p-3 border border-warning-100">
                                                {reservation.note}
                                            </p>
                                        ) : (
                                            <p className="text-sm text-gray-400 italic">Žiadna poznámka</p>
                                        )}
                                    </div>
                                </div>
                            }
                            onSave={(val) => this.props.onUpdate({ note: val })}
                            renderInput={({ save, cancel }) => (
                                <NoteEditor value={reservation.note} onSave={save} onCancel={cancel} />
                            )}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

// Helper Components for Inline Editors
// Implements the specific edit UIs with local state

const EditorActions = ({ onSave, onCancel }) => (
    <div className="flex justify-end gap-2 mt-2">
        <button onClick={onCancel} className="p-1 text-gray-500 hover:bg-gray-100 rounded">
            <X size={18} />
        </button>
        <button onClick={onSave} className="p-1 text-white bg-success-600 hover:bg-success-700 rounded shadow-sm">
            <Check size={18} />
        </button>
    </div>
);

const DateRangeEditor = ({ startDate, endDate, onSave, onCancel }) => {
    const [start, setStart] = React.useState(startDate);
    const [end, setEnd] = React.useState(endDate);

    const handleSave = () => {
        onSave({ start_date: start, end_date: end });
    };

    return (
        <div className="bg-white p-3 border border-primary-200 rounded-lg shadow-sm">
            <div className="space-y-3">
                <DatePicker
                    label="Od"
                    value={moment(start)}
                    onChange={(d) => {
                        const newStart = d?.toDate();
                        setStart(newStart);
                        // Auto-adjust end if start > end
                        if (newStart > end) setEnd(newStart);
                    }}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
                <DatePicker
                    label="Do"
                    value={moment(end)}
                    onChange={(d) => {
                        const newEnd = d?.toDate();
                        // Prevent end < start
                        if (newEnd < start) {
                            setEnd(start);
                        } else {
                            setEnd(newEnd);
                        }
                    }}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
            </div>
            <EditorActions onSave={handleSave} onCancel={onCancel} />
        </div>
    )
}

const BoatsEditor = ({ kanoeMin, kanoeMax, katamaran, onSave, onCancel }) => {
    const [kMin, setKMin] = React.useState(kanoeMin);
    const [kMax, setKMax] = React.useState(kanoeMax);
    const [kat, setKat] = React.useState(katamaran);

    // Enforce Min <= Max logic
    const handleMinChange = (val) => {
        const v = parseInt(val) || 0;
        setKMin(v);
        setKMax(v); // Auto-sync Max to Min (User request)
    }

    const handleMaxChange = (val) => {
        const v = parseInt(val) || 0;
        if (v < kMin) {
            setKMax(kMin);
        } else {
            setKMax(v);
        }
    }

    const handleSave = () => {
        onSave({
            kanoe_count_info_min: kMin,
            kanoe_count_info_max: kMax,
            katamaran_count_info: kat
        });
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSave();
        }
    }

    return (
        <div className="bg-white p-3 border border-primary-200 rounded-lg shadow-sm">
            <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                    <span className="text-xs text-gray-500">Kanoe Min</span>
                    <input
                        type="number"
                        min="0"
                        value={kMin}
                        onChange={e => handleMinChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full border rounded p-1"
                        autoFocus
                    />
                </div>
                <div>
                    <span className="text-xs text-gray-500">Kanoe Max</span>
                    <input
                        type="number"
                        min="0"
                        value={kMax}
                        onChange={e => handleMaxChange(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full border rounded p-1"
                    />
                </div>
            </div>
            <div>
                <span className="text-xs text-gray-500">Katamarán</span>
                <input
                    type="number"
                    min="0"
                    value={kat}
                    onChange={e => setKat(parseInt(e.target.value) || 0)}
                    onKeyDown={handleKeyDown}
                    className="w-full border rounded p-1"
                />
            </div>
            <EditorActions onSave={handleSave} onCancel={onCancel} />
        </div>
    )
}

const PlaceEditor = ({ label, value, places, onSave, onCancel }) => {
    const [val, setVal] = React.useState(value || '');

    // Reuse the unique ID for datalist to avoid conflicts if multiple open (though unlikely in modal)
    const listId = `places-list-${label}`;

    return (
        <div className="bg-white p-3 border border-primary-200 rounded-lg shadow-sm">
            <div className="mb-2 text-xs font-bold text-gray-700">{label}</div>

            <input
                type="text"
                list={listId}
                value={val}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        onSave(val);
                    }
                }}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors text-sm"
                autoFocus
                placeholder={label}
            />

            <datalist id={listId}>
                {places && places.map((p, i) => (
                    <option key={i} value={p.name} />
                ))}
            </datalist>

            <EditorActions onSave={() => onSave(val)} onCancel={onCancel} />
        </div>
    )
}

const NoteEditor = ({ value, onSave, onCancel }) => {
    const [val, setVal] = React.useState(value);
    return (
        <div className="bg-white p-3 border border-primary-200 rounded-lg shadow-sm w-full">
            <textarea
                className="w-full border rounded p-2 min-h-[80px]"
                value={val}
                onChange={e => setVal(e.target.value)}
                placeholder="Poznámka..."
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                        onSave(val);
                    }
                }}
            />
            <div className="text-[10px] text-gray-400 mt-1 text-right">Ctrl+Enter na uloženie</div>
            <EditorActions onSave={() => onSave(val)} onCancel={onCancel} />
        </div>
    )
}


export default ReservationData;
